import { CTA } from '@volkswagen-onehub/components-core';
import {
  Gallery as GalleryIcon,
  Interior360,
  View360,
} from '@volkswagen-onehub/icons-core';
import { ReactElement, useMemo, useState } from 'react';
import {
  CAPI_EXTERIOR_360_IMAGE_RELS,
  CAPI_GALLERY_IMAGE_RELS,
  CAPI_INTERIOR_360_IMAGE_REL,
} from '@/constants/images';
import useImageLoader from '@/hooks/useImageLoader';
import useResizeResponsive from '@/hooks/useResizeResponsive';
import {
  Box,
  CTAGroup,
  GalleryViewerCarousel,
  GalleryViewer as GalleryViewerComponent,
  PanoramaViewer,
  ProductViewer360,
  VWImageQuality,
} from '@/modules/components';
import { isDesktop } from '@/modules/utils/breakpoints';
import { filterImageByRel, filterImagesByRel } from '@/modules/utils/image';
import { getActiveConfiguration } from '@/modules/utils/storeUtils';
import useGlobalStore from '@/stores/globalStore';
import { StyledGalleryViewerCarousel } from './GalleryViewer.styles';
import { performVWBasicButtonClick } from '@/analytics/events';
import { useTribalContext } from '@/context/tribal';
import { SectionNames } from '@/analytics/sectionNames';

interface GalleryViewerProps {
  onBack: () => void;
}

export const GalleryViewer = ({ onBack }: GalleryViewerProps) => {
  const { analyticsManager, analyticsBuilder } = useTribalContext();
  const [selected, setSelected] = useState(0);
  const configuration = useGlobalStore((state) =>
    getActiveConfiguration(state.app)
  );
  const { retailers } = useGlobalStore((state) => state.app);

  const { isLoading, validImages } = useImageLoader(
    configuration?.pdbData?.capi?.images || [],
    {
      quality: VWImageQuality.HIGH,
    }
  );

  const isDesktopWidth = useResizeResponsive(isDesktop);

  const sliderProps = useMemo(
    () => ({
      arrows: isDesktopWidth,
    }),
    [isDesktopWidth]
  );

  const handleSelect = (id: number, el: ReactElement) => {
    setSelected(id);
    performVWBasicButtonClick({
      label: el.props.ariaLabel,
      sectionName: SectionNames.SINGLE_PAGE_GALLERY_VIEWER,
      configuration,
      analyticsManager,
      analyticsBuilder,
      retailers,
    });
  };

  const carouselImages = filterImagesByRel(
    validImages,
    CAPI_GALLERY_IMAGE_RELS
  );

  const exterior360Images = filterImagesByRel(
    validImages,
    CAPI_EXTERIOR_360_IMAGE_RELS
  );

  const interior360Image = filterImageByRel(
    validImages,
    CAPI_INTERIOR_360_IMAGE_REL
  );

  return (
    <GalleryViewerComponent
      tabs={
        <>
          <CTAGroup activeIndex={selected} onCtaClick={handleSelect}>
            {!!carouselImages.length && (
              <CTA
                tag="button"
                icon={<GalleryIcon />}
                aria-label="view-gallery"
              />
            )}
            {!!exterior360Images.length && (
              <CTA
                tag="button"
                icon={<View360 />}
                aria-label="view-exterior-360"
              />
            )}
            {interior360Image && (
              <CTA
                tag="button"
                icon={<Interior360 />}
                aria-label="view-interior-360"
              />
            )}
          </CTAGroup>
        </>
      }
      actions={
        <>
          {isDesktopWidth ? (
            <CTA tag="button" emphasis="primary" onClick={onBack}>
              Back
            </CTA>
          ) : (
            <CTA
              tag="button"
              emphasis="primary"
              icon={<>&lt;</>}
              onClick={onBack}
            />
          )}
        </>
      }
      width="100%"
      isLoading={isLoading}
    >
      {selected === 0 && !!carouselImages.length && (
        <StyledGalleryViewerCarousel>
          <GalleryViewerCarousel {...sliderProps}>
            {carouselImages.map(({ title, uri }) => (
              <img
                src={uri}
                alt={title}
                style={{ maxHeight: '2000px' }}
                key={uri}
              />
            ))}
          </GalleryViewerCarousel>
        </StyledGalleryViewerCarousel>
      )}
      {selected === 1 && (
        <ProductViewer360 width="100%">
          {exterior360Images.map(({ title, uri }) => (
            <img src={uri} alt={title} key={uri} />
          ))}
        </ProductViewer360>
      )}
      {selected === 2 && (
        <Box
          height="512px"
          b960={{
            height: '715px',
          }}
          b1280={{
            height: '895px',
          }}
        >
          <PanoramaViewer
            src={interior360Image.uri}
            width="100%"
            height="100%"
            defaultZoomLvl={30}
            defaultYaw={0.5}
            defaultPitch={-0.3}
          />
        </Box>
      )}
    </GalleryViewerComponent>
  );
};

export default GalleryViewer;
